import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle2,
  TrendingUp,
  Target,
  BarChart3,
  Settings,
  Award,
  ChevronRight,
  Menu,
  X,
  ArrowUp,
  Mail,
  Linkedin,
  Quote,
} from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import StickyCTA from '../components/StickyCTA';
import FAQSection from '../components/FAQSection';
import { openCalendly as trackedOpenCalendly } from '../lib/analytics';
import { siteData } from '../mock';

const CALENDLY_LINK = 'https://calendly.com/hitesh-ejqa/ppc-discussion';

const BRAND_LOGOS = [
  { src: '/brands/good-good.png', alt: 'Good Good' },
  { src: '/brands/food-huggers.webp', alt: 'Food Huggers' },
  { src: '/brands/iprimio.png', alt: 'iPrimio' },
  { src: '/brands/sliptogrip.jpg', alt: 'Slip to Grip' },
  { src: '/brands/touch.webp', alt: 'Touch Skin Care' },
  { src: '/brands/ocushield.webp', alt: 'Ocushield' },
  { src: '/brands/perch.png', alt: 'Perch' },
  { src: '/brands/upscalio.webp', alt: 'Upscalio' },
  { src: '/brands/below-zero.png', alt: 'Below Zero' },
  { src: '/brands/premium-guard.png', alt: 'Premium Guard' },
];

const Home = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setIsMobileMenuOpen(false);
  };

  const openCalendly = (source = 'home') => {
    trackedOpenCalendly(source);
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { id: 'services', label: 'Services' },
    { id: 'process', label: 'Process' },
    { id: 'about', label: 'About' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="home-root">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md">Skip to main content</a>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={scrollToTop}
              className="flex items-center gap-3"
              data-testid="nav-logo-btn"
            >
              <img
                src="/logo.png"
                alt="First Page Consultants"
                className="h-10 sm:h-11 w-auto"
              />
              <span className="hidden sm:inline-block font-extrabold tracking-tight text-foreground text-base lg:text-lg">
                First Page <span className="text-primary">Consultants</span>
              </span>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  data-testid={`nav-${link.id}-btn`}
                  className="text-muted-foreground hover:text-primary transition-colors font-medium"
                >
                  {link.label}
                </button>
              ))}
              <ThemeToggle />
              <Button
                onClick={openCalendly}
                data-testid="nav-cta-btn"
                className="bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold btn-brand-glow"
              >
                Book a Strategy Call
              </Button>
            </nav>

            {/* Mobile controls */}
            <div className="flex items-center gap-2 lg:hidden">
              <ThemeToggle />
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                data-testid="mobile-menu-toggle"
                className="p-2 text-foreground hover:text-primary transition-colors"
                aria-label="Open menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-2">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  data-testid={`mobile-nav-${link.id}-btn`}
                  className="block w-full text-left px-4 py-2 text-foreground hover:bg-accent hover:text-accent-foreground rounded-lg transition-colors font-medium"
                >
                  {link.label}
                </button>
              ))}
              <Button
                onClick={openCalendly}
                data-testid="mobile-nav-cta-btn"
                className="w-full bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold"
              >
                Book a Strategy Call
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <main id="main">
      <section className="relative overflow-hidden py-12 sm:py-16 md:py-24 lg:py-32 bg-gradient-to-b from-[hsl(var(--brand-soft))] to-background">
        {/* Decorative grid */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07] dark:opacity-[0.12]"
          style={{
            backgroundImage:
              'linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at top, black 40%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at top, black 40%, transparent 75%)',
          }}
        />
        <div className="container mx-auto px-4 sm:px-6 relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6 sm:space-y-8 animate-fade-up">
              <Badge className="bg-accent text-accent-foreground hover:bg-accent border-0 px-3 py-1 text-xs font-semibold tracking-wide uppercase">
                Amazon Ads & Operations · Est. 2016
              </Badge>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.1]">
                {siteData.hero.headline}
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl">
                {siteData.hero.subheadline}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  onClick={openCalendly}
                  size="lg"
                  data-testid="hero-cta-primary-btn"
                  className="bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto font-semibold btn-brand-glow"
                >
                  Book a Strategy Call
                </Button>
                <Button
                  onClick={() => scrollToSection('process')}
                  size="lg"
                  variant="outline"
                  data-testid="hero-cta-secondary-btn"
                  className="text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 border-2 border-border hover:border-primary hover:text-primary bg-transparent w-full sm:w-auto font-semibold"
                >
                  See How We Work
                </Button>
              </div>
              <p className="text-sm font-semibold text-foreground pt-2 sm:pt-4">
                {siteData.hero.trustLine}
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-3 pt-2">
                {siteData.hero.credibility.map((item, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="px-3 py-1.5 bg-accent text-accent-foreground hover:bg-accent text-xs sm:text-sm font-medium border border-border"
                  >
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border">
                <img
                  src={siteData.hero.heroImage}
                  alt="Amazon Ads and account management team working with a US brand"
                  width="1200"
                  height="800"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent" />
              </div>
              {/* Floating stat card */}
              <div className="absolute -bottom-6 -left-6 bg-card border border-border rounded-xl shadow-xl p-5 max-w-xs hidden xl:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-extrabold text-foreground">$500k+</p>
                    <p className="text-xs text-muted-foreground">Monthly ad spend managed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brands Marquee */}
      <section className="py-12 sm:py-16 bg-background border-y border-border">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-2 sm:mb-3">
              Trusted by Growing Brands
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              Some of the fastest growing Amazon brands trust us to own and grow their presence
            </p>
          </div>

          <div className="relative overflow-hidden">
            <div className="marquee-mask-left absolute left-0 top-0 bottom-0 w-16 sm:w-32 z-10" />
            <div className="marquee-mask-right absolute right-0 top-0 bottom-0 w-16 sm:w-32 z-10" />

            <div className="flex gap-12 animate-marquee">
              {[...BRAND_LOGOS, ...BRAND_LOGOS].map((brand, idx) => (
                <div
                  key={`${brand.alt}-${idx}`}
                  className="brand-chip flex-shrink-0 w-44 h-24 sm:w-48 sm:h-28 flex items-center justify-center rounded-xl px-4"
                >
                  <img
                    src={brand.src}
                    alt={`${brand.alt} — Amazon brand client of First Page Consultants`}
                    loading="lazy"
                    decoding="async"
                    className="max-h-16 sm:max-h-20 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 sm:py-16 md:py-20 bg-[hsl(var(--surface-subtle))]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 sm:mb-14">
              <Badge className="bg-accent text-accent-foreground hover:bg-accent border-0 px-3 py-1 text-xs font-semibold tracking-wide uppercase mb-4">
                What Clients Say
              </Badge>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                Operators, not order-takers
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground mt-3 max-w-2xl mx-auto">
                Real feedback from brands we run Amazon Ads and operations for.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
              {siteData.testimonials.map((t, i) => (
                <Card
                  key={i}
                  data-testid={`testimonial-card-${i}`}
                  className="relative bg-card border border-border hover-lift overflow-hidden"
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-1"
                    style={{
                      background:
                        'linear-gradient(90deg, hsl(var(--brand)), hsl(var(--brand-hover)))',
                    }}
                  />
                  <CardContent className="p-6 sm:p-8 flex flex-col h-full">
                    <Quote className="w-8 h-8 text-primary/30 mb-4" />
                    <p className="text-foreground/90 text-base sm:text-[17px] leading-relaxed flex-1">
                      &ldquo;{t.quote}&rdquo;
                    </p>
                    <div className="mt-6 pt-6 border-t border-border flex items-center gap-4">
                      <div className="brand-chip w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                        <img
                          src={t.logo}
                          alt={`${t.brand} logo`}
                          loading="lazy"
                          decoding="async"
                          className="max-h-8 max-w-10 object-contain"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-foreground truncate">{t.name}</p>
                        <p className="text-sm text-muted-foreground truncate">
                          {t.title} · <span className="text-primary font-semibold">{t.brand}</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-8 sm:space-y-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
              {siteData.problem.headline}
            </h2>
            <div className="space-y-3 sm:space-y-4">
              {siteData.problem.painPoints.map((point, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 sm:gap-4 text-left p-4 sm:p-6 bg-muted/60 rounded-xl border border-border"
                >
                  <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 bg-destructive/10 rounded-full flex items-center justify-center">
                    <span className="text-destructive font-bold text-sm sm:text-base">
                      {index + 1}
                    </span>
                  </div>
                  <p className="text-base sm:text-lg text-foreground/90 flex-1">{point}</p>
                </div>
              ))}
            </div>
            <p className="text-lg sm:text-xl font-semibold text-foreground pt-4 sm:pt-8">
              {siteData.problem.closeLine}
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-[hsl(var(--surface-subtle))]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto space-y-8 sm:space-y-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground text-center">
              {siteData.solution.headline}
            </h2>
            <div className="space-y-3 sm:space-y-4">
              {siteData.solution.pillars.map((pillar, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-card rounded-xl shadow-sm hover-lift border border-border"
                >
                  <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-base sm:text-lg text-foreground/90">{pillar}</p>
                </div>
              ))}
            </div>
            <p className="text-lg sm:text-xl font-semibold text-foreground text-center pt-4 sm:pt-8">
              {siteData.solution.closeLine}
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-12 sm:py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto space-y-8 sm:space-y-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground text-center">
              What We Do
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {siteData.services.map((service, index) => {
                const icons = [TrendingUp, Target, BarChart3, Settings, Award];
                const Icon = icons[index % icons.length];
                return (
                  <Card
                    key={index}
                    data-testid={`service-card-${index}`}
                    className="hover-lift border border-border hover:border-primary/50 bg-card"
                  >
                    <CardHeader>
                      <div className="w-11 h-11 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-3 sm:mb-4">
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                      </div>
                      <CardTitle className="text-lg sm:text-xl font-bold text-foreground">
                        {service.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {service.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="process" className="py-12 sm:py-16 md:py-20 bg-[hsl(var(--surface-subtle))]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto space-y-8 sm:space-y-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground text-center">
              How It Works
            </h2>
            <div className="relative">
              <div className="hidden md:block absolute left-6 lg:left-8 top-0 bottom-0 w-0.5 bg-primary/25" />
              <div className="space-y-6 sm:space-y-8">
                {siteData.process.map((step) => (
                  <div key={step.step} className="relative flex items-start gap-4 sm:gap-6">
                    <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl sm:text-2xl font-bold shadow-lg shadow-primary/20 z-10 ring-4 ring-background">
                      {step.step}
                    </div>
                    <div className="flex-1 bg-card p-4 sm:p-6 rounded-xl shadow-sm hover-lift border border-border">
                      <h3 className="text-lg sm:text-xl font-bold text-foreground mb-2">
                        {step.title}
                      </h3>
                      <p className="text-sm sm:text-base text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                {siteData.tools.headline}
              </h2>
              <p className="text-xl text-muted-foreground">{siteData.tools.intro}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {siteData.tools.toolsList.map((tool, index) => (
                <Card
                  key={index}
                  className="text-center hover-lift border border-border bg-card"
                >
                  <CardHeader>
                    <CardTitle className="text-2xl font-bold text-primary">{tool.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground">
                      {tool.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-xl font-semibold text-foreground text-center pt-8">
              {siteData.tools.supportLine}
            </p>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="py-20 bg-[hsl(var(--surface-subtle))]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground text-center">
              {siteData.why.headline}
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {siteData.why.differentiators.map((diff, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-6 bg-card rounded-xl shadow-sm hover-lift border border-border"
                >
                  <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0" />
                  <p className="text-lg font-semibold text-foreground">{diff}</p>
                </div>
              ))}
            </div>
            <p className="text-xl font-semibold text-foreground text-center pt-8">
              {siteData.why.closeLine}
            </p>
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {siteData.proof.map((item, index) => (
                <div
                  key={index}
                  className="text-center p-6 bg-card rounded-xl border border-border hover-lift"
                >
                  <Award className="w-12 h-12 text-primary mx-auto mb-4" />
                  <p className="text-base font-semibold text-foreground">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Qualification */}
      <section className="py-20 bg-[hsl(var(--surface-subtle))]">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-foreground">This is for you if:</h3>
                <div className="space-y-4">
                  {siteData.qualification.forYou.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 bg-primary/5 rounded-xl border border-primary/30"
                    >
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                      <p className="text-foreground">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-foreground">This is not for you if:</h3>
                <div className="space-y-4">
                  {siteData.qualification.notForYou.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 bg-destructive/5 rounded-xl border border-destructive/30"
                    >
                      <ChevronRight className="w-6 h-6 text-destructive flex-shrink-0 mt-0.5" />
                      <p className="text-foreground">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 sm:py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3 sm:mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground">
                No upsells. No hidden fees. Just clear, honest pricing.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
              {/* PPC Management */}
              <Card
                data-testid="pricing-ppc-card"
                className="relative border border-border hover:border-primary transition-all duration-300 hover-lift bg-card overflow-hidden"
              >
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-lg">
                  MOST POPULAR
                </div>
                <CardHeader className="text-center pb-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                    PPC Management
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-3xl sm:text-4xl font-black text-primary">$799</span>
                    <span className="text-muted-foreground text-base sm:text-lg">/month</span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-2">
                    Clean retainer. No upsells.
                  </p>
                </CardHeader>
                <CardContent className="text-center">
                  <ul className="space-y-3 text-left">
                    {[
                      'Full Amazon PPC management',
                      'Daily optimization & monitoring',
                      'Weekly performance reports',
                      'Sponsored Products, Brands & Display',
                    ].map((feat) => (
                      <li key={feat} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm sm:text-base text-foreground/90">{feat}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={openCalendly}
                    data-testid="pricing-ppc-cta"
                    className="w-full mt-6 bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold btn-brand-glow"
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>

              {/* Listing Optimization */}
              <Card
                data-testid="pricing-listing-card"
                className="border border-border hover:border-primary transition-all duration-300 hover-lift bg-card"
              >
                <CardHeader className="text-center pb-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Settings className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                    Account & Listing Optimization
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-3xl sm:text-4xl font-black text-primary">$500</span>
                    <span className="text-muted-foreground text-base sm:text-lg">/month</span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-2">
                    Clean retainer. No hidden fees.
                  </p>
                </CardHeader>
                <CardContent className="text-center">
                  <ul className="space-y-3 text-left">
                    {[
                      'Complete account setup & optimization',
                      'Listing optimization for conversions',
                      'Keyword research & content strategy',
                      'Brand Registry & account health',
                    ].map((feat) => (
                      <li key={feat} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm sm:text-base text-foreground/90">{feat}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={openCalendly}
                    data-testid="pricing-listing-cta"
                    className="w-full mt-6 bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold btn-brand-glow"
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            </div>

            <p className="text-center text-sm sm:text-base text-muted-foreground mt-8 sm:mt-12">
              All plans include direct access to senior Amazon operators. No junior handoffs.
            </p>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section id="about" className="py-20 bg-[hsl(var(--surface-subtle))]">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border">
                  <img
                    src={siteData.founder.image}
                    alt={siteData.founder.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-primary text-primary-foreground rounded-xl px-4 py-3 shadow-xl hidden sm:block">
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-90">Est.</p>
                  <p className="text-2xl font-black">2016</p>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                    {siteData.founder.name}
                  </h2>
                  <p className="text-xl text-primary font-semibold">{siteData.founder.title}</p>
                </div>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {siteData.founder.bio}
                </p>
                <Button
                  onClick={openCalendly}
                  size="lg"
                  data-testid="founder-cta-btn"
                  className="bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold btn-brand-glow"
                >
                  Book a Strategy Call
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ (visible content matching the FAQPage schema) */}
      <FAQSection
        headline="Frequently Asked Questions"
        subhead="What Amazon brands ask us before booking a strategy call."
        faqs={siteData.faqs}
        source="home_faq"
      />

      {/* Final CTA */}
      <section
        id="contact"
        className="relative py-20 text-primary-foreground overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, hsl(var(--brand)) 0%, hsl(var(--brand-hover)) 100%)',
        }}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.3) 0%, transparent 40%)',
          }}
        />
        <div className="container mx-auto px-6 relative">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-4xl font-bold">{siteData.finalCTA.headline}</h2>
            <p className="text-xl opacity-95">{siteData.finalCTA.supportLine}</p>
            <Button
              onClick={openCalendly}
              size="lg"
              data-testid="final-cta-btn"
              className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6 font-semibold"
            >
              Book a Strategy Call
            </Button>
            <p className="text-sm opacity-90 pt-4">{siteData.finalCTA.microCopy}</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      </main>
      <footer className="bg-[hsl(222,47%,8%)] dark:bg-[hsl(222,47%,4%)] text-white py-8 sm:py-12 border-t border-border">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="First Page Consultants" className="h-10 sm:h-12 w-auto" />
                <span className="font-extrabold tracking-tight text-white">
                  First Page <span className="text-primary">Consultants</span>
                </span>
              </div>
              <p className="text-sm sm:text-base text-white/70">{siteData.footer.description}</p>
              <div className="flex items-center gap-2 text-sm sm:text-base text-white/70">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                <a
                  href="mailto:teams@firstpageconsultants.com"
                  className="hover:text-white transition-colors"
                >
                  teams@firstpageconsultants.com
                </a>
              </div>
              <div className="flex items-center gap-2 text-sm sm:text-base text-white/70">
                <Linkedin className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                <a
                  href="https://www.linkedin.com/company/first-page-consultants"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  LinkedIn
                </a>
              </div>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <h4 className="text-base sm:text-lg font-semibold">Quick Links</h4>
              <div className="flex flex-col space-y-2">
                {navLinks.slice(0, 3).map((link) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="text-sm sm:text-base text-white/70 hover:text-white transition-colors text-left"
                  >
                    {link.label}
                  </button>
                ))}
                <button className="text-sm sm:text-base text-white/70 hover:text-white transition-colors text-left">
                  Privacy Policy
                </button>
              </div>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <h4 className="text-base sm:text-lg font-semibold">Contact</h4>
              <p className="text-sm sm:text-base text-white/70">{siteData.footer.location}</p>
              <Button
                onClick={openCalendly}
                data-testid="footer-cta-btn"
                className="bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground font-semibold w-full sm:w-auto"
              >
                Get in Touch
              </Button>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 sm:mt-12 pt-6 sm:pt-8 text-center text-sm sm:text-base text-white/50">
            <p>
              &copy; {new Date().getFullYear()} {siteData.footer.companyName}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky Call CTA (mobile bar + desktop pill) */}
      <StickyCTA source="home" />

      {/* Scroll to Top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          data-testid="scroll-top-btn"
          className="fixed bottom-24 right-6 z-40 bg-primary hover:bg-[hsl(var(--brand-hover))] text-primary-foreground p-3 rounded-full shadow-lg shadow-primary/30 transition-all duration-300 hover:scale-110 hidden md:block"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default Home;
